/**
 * SF-04 «Статус жалобы» и SF-08 «Статус апелляции».
 *
 * ПС §14: пользователю показывается состояние и даты — без деталей решения
 * и без внутренних сигналов модерации (BS §15.6). Списка обращений в
 * контракте нет (GAP-ALL-04, GAP-ALL-05), поэтому идентификатор последнего
 * обращения клиент запоминает у себя при отправке и ведёт по нему.
 */
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { Appeal, Report } from '@shared/api/types'
import { Banner, Button, EmptyState, Icon, Spinner, cx } from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import { formatDateTime } from '../../lib/format'
import styles from './status.module.css'

const REPORT_STATE: Record<Report['state'], { title: string; text: string; tone: string }> = {
  received: {
    title: 'Жалоба принята',
    text: 'Обращение в очереди на рассмотрение.',
    tone: 'stateNeutral',
  },
  triaged: {
    title: 'Рассматривается',
    text: 'Модератор изучает обращение. Это может занять до нескольких дней.',
    tone: 'stateProgress',
  },
  resolved: {
    title: 'Решение принято',
    text: 'Обращение рассмотрено. Подробности решения не раскрываем.',
    tone: 'stateDone',
  },
}

const APPEAL_STATE: Record<Appeal['state'], { title: string; text: string; tone: string }> = {
  open: {
    title: 'Апелляция на рассмотрении',
    text: 'Апелляция принята. Ответим уведомлением.',
    tone: 'stateProgress',
  },
  accepted: {
    title: 'Апелляция удовлетворена',
    text: 'Ограничение снято. Если оно ещё отображается — обновите приложение.',
    tone: 'stateDone',
  },
  rejected: {
    title: 'Апелляция отклонена',
    text: 'Решение по ограничению осталось в силе.',
    tone: 'stateDanger',
  },
}

export function ReportStatusScreen(): React.ReactElement {
  const { reportId } = useParams({ strict: false }) as { reportId: string }

  const query = useQuery({
    queryKey: ['report', reportId],
    queryFn: async () => unwrap(await api.GET('/reports/{reportId}', { params: { path: { reportId } } })),
    retry: false,
  })

  return (
    <StatusLayout
      title="Обращение"
      loading={query.isLoading}
      error={query.error}
      state={query.data ? REPORT_STATE[query.data.state] : null}
      createdAt={query.data?.created_at}
      closedAt={query.data?.resolved_at ?? null}
      closedLabel="Рассмотрено"
    />
  )
}

export function AppealStatusScreen(): React.ReactElement {
  const { appealId } = useParams({ strict: false }) as { appealId: string }

  const query = useQuery({
    queryKey: ['appeal', appealId],
    queryFn: async () => unwrap(await api.GET('/appeals/{appealId}', { params: { path: { appealId } } })),
    retry: false,
  })

  return (
    <StatusLayout
      title="Апелляция"
      loading={query.isLoading}
      error={query.error}
      state={query.data ? APPEAL_STATE[query.data.state] : null}
      createdAt={query.data?.created_at}
      closedAt={query.data?.decided_at ?? null}
      closedLabel="Решение принято"
    />
  )
}

/* ===================================================================== */
/* Общая раскладка                                                        */
/* ===================================================================== */

function StatusLayout({
  title,
  loading,
  error,
  state,
  createdAt,
  closedAt,
  closedLabel,
}: {
  title: string
  loading: boolean
  error: unknown
  state: { title: string; text: string; tone: string } | null
  createdAt: string | undefined
  closedAt: string | null
  closedLabel: string
}): React.ReactElement {
  const navigate = useNavigate()

  if (loading) {
    return (
      <Screen title={title} back narrow>
        <Spinner size={28} label="Загружаем" />
      </Screen>
    )
  }

  // 404: обращение не найдено — это не ошибка приложения.
  if (error || !state) {
    const notFound = error instanceof ApiError && error.code === 'not_found'

    return (
      <Screen title={title} back narrow>
        <EmptyState
          icon={notFound ? 'info' : 'alert'}
          title={notFound ? 'Обращение не найдено' : 'Не удалось загрузить'}
          text={
            notFound
              ? 'Возможно, ссылка устарела. Если вопрос остался — напишите в поддержку.'
              : 'Проверьте соединение и попробуйте ещё раз.'
          }
          actions={
            <Button variant="primary" onClick={() => void navigate({ to: '/settings/support/new' })}>
              Написать в поддержку
            </Button>
          }
        />
      </Screen>
    )
  }

  return (
    <Screen title={title} back narrow>
      <div className={cx(styles['state'], styles[state.tone])}>
        <Icon name={closedAt ? 'check' : 'clock'} size={26} />
        <div>
          <p className={styles['stateTitle']}>{state.title}</p>
          <p className={styles['stateText']}>{state.text}</p>
        </div>
      </div>

      <dl className={styles['dates']}>
        {createdAt ? (
          <div className={styles['dateRow']}>
            <dt>Отправлено</dt>
            <dd>{formatDateTime(createdAt)}</dd>
          </div>
        ) : null}
        {closedAt ? (
          <div className={styles['dateRow']}>
            <dt>{closedLabel}</dt>
            <dd>{formatDateTime(closedAt)}</dd>
          </div>
        ) : null}
      </dl>

      <Banner tone="neutral" icon="shield">
        Кто пожаловался — не раскрываем, детали решения — тоже.
      </Banner>
    </Screen>
  )
}
